<template>
  <b-form @submit.prevent="submit">
    <b-form-group
      label="ชื่อระดับ *"
      label-cols-sm="3"
      label-size="sm"
      class="mb-2"
      :state="nameErr ? false : null"
      :invalid-feedback="nameErr"
    >
      <b-form-input
        v-model="form.name"
        size="sm"
        data-testid="tier-form-name"
        :state="nameErr ? false : null"
      />
    </b-form-group>

    <b-form-group
      label="ลำดับ *"
      label-cols-sm="3"
      label-size="sm"
      class="mb-2"
      :state="rankErr ? false : null"
      :invalid-feedback="rankErr"
    >
      <b-form-input
        v-model.number="form.rank"
        type="number"
        min="1"
        size="sm"
        data-testid="tier-form-rank"
        :state="rankErr ? false : null"
      />
      <small class="text-muted">ลำดับจากน้อยไปมาก แต่ละระดับต้องมีลำดับไม่ซ้ำกัน</small>
    </b-form-group>

    <b-form-group
      label="ยอดขั้นต่ำ (บาท) *"
      label-cols-sm="3"
      label-size="sm"
      class="mb-2"
      :state="thresholdErr ? false : null"
      :invalid-feedback="thresholdErr"
    >
      <b-form-input
        v-model.number="form.thresholdAmount"
        type="number"
        min="0"
        step="0.01"
        size="sm"
        data-testid="tier-form-threshold"
        :state="thresholdErr ? false : null"
      />
      <small class="text-muted">ยอดใช้จ่ายสะสมภายในช่วงเวลาที่กำหนด (ตั้งค่าได้ที่การตั้งค่าระบบระดับลูกค้า) ที่ต้องถึงเพื่อได้ระดับนี้</small>
    </b-form-group>

    <b-form-group label="ชุดบัตรกำนัลเมื่อขึ้นระดับ" label-cols-sm="3" label-size="sm" class="mb-2">
      <b-form-select v-model="form.voucherBatchId" :options="batchOptions" size="sm" />
      <small class="text-muted">ไม่เลือก = ไม่แจกบัตรกำนัลเมื่อลูกค้าขึ้นถึงระดับนี้</small>
    </b-form-group>

    <b-form-group v-if="isEdit" label="สถานะ" label-cols-sm="3" label-size="sm" class="mb-2">
      <b-form-checkbox v-model="form.isActive" switch>
        {{ form.isActive ? "ใช้งาน" : "ปิดใช้งาน" }}
      </b-form-checkbox>
    </b-form-group>

    <b-alert :show="!!errorMessage" variant="danger">{{ errorMessage }}</b-alert>

    <div class="d-flex justify-content-end">
      <b-button variant="light" size="sm" class="mr-2" @click="$emit('cancel')">ยกเลิก</b-button>
      <b-button
        type="submit"
        variant="primary"
        size="sm"
        data-testid="tier-form-submit"
        :disabled="!canSubmit"
      >
        {{ isEdit ? "บันทึกการแก้ไข" : "สร้างระดับ" }}
      </b-button>
    </div>
  </b-form>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import {
  rankError as rankErrorOf,
  thresholdAmountError as thresholdAmountErrorOf,
} from "@/utils/tierValidation";
import { tierErrorMessage } from "@/utils/tierErrors";

export default {
  name: "TierForm",
  props: {
    // Pass an existing tier to edit it; omit (or null) to create a new one.
    tier: { type: Object, default: null },
  },
  data() {
    const t = this.tier;
    return {
      isSaving: false,
      errorMessage: "",
      form: {
        name: t ? t.name : "",
        rank: t ? t.rank : null,
        thresholdAmount: t ? t.thresholdAmount : null,
        voucherBatchId: t ? t.voucherBatchId ?? null : null,
        isActive: t ? !!t.isActive : true,
      },
    };
  },
  computed: {
    ...mapGetters("moduleVoucher", ["getBatchList"]),
    clinicUrl() {
      return this.$route.params.clinicUrl;
    },
    isEdit() {
      return !!this.tier;
    },
    // Any batch belonging to another clinic is never in this list in the
    // first place — fetchBatches is itself clinic-scoped by clinicUrl (see
    // src/stores/voucher.js), and the backend rejects a cross-clinic
    // voucherBatchId outright (createTier.js / updateTier.js).
    //
    // Status is a different story: createTier/updateTier only check clinic
    // ownership, not batch.status, so a DRAFT or CLOSED batch saves onto the
    // tier without complaint. The rejection only happens later and silently
    // — claimTierVoucher (tier_service) checks `batch.status !== "ACTIVE"`
    // and just returns null, so a patient upgrades into the tier with no
    // voucher and nothing tells anyone why. Filter the picker down to ACTIVE
    // batches so that footgun isn't offered in the first place. The one
    // exception: keep whatever batch this tier is already pointing at in the
    // list even if it's no longer ACTIVE, so editing a tier doesn't silently
    // drop an existing (if now-stale) selection out of the dropdown.
    batchOptions() {
      const options = [{ value: null, text: "ไม่ระบุ" }];
      for (const batch of this.getBatchList || []) {
        if (batch.status === "ACTIVE" || batch.id === this.form.voucherBatchId) {
          options.push({ value: batch.id, text: `${batch.name} (${batch.codePrefix})` });
        }
      }
      return options;
    },
    nameErr() {
      return this.form.name && this.form.name.trim() ? "" : "กรุณาระบุชื่อระดับ";
    },
    rankErr() {
      return rankErrorOf(this.form.rank);
    },
    thresholdErr() {
      return thresholdAmountErrorOf(this.form.thresholdAmount);
    },
    canSubmit() {
      return !this.isSaving && !this.nameErr && !this.rankErr && !this.thresholdErr;
    },
  },
  methods: {
    ...mapActions("moduleTier", ["createTier", "updateTier"]),
    ...mapActions("moduleVoucher", ["fetchBatches"]),

    async submit() {
      if (!this.canSubmit) return;
      this.isSaving = true;
      this.errorMessage = "";
      try {
        if (this.isEdit) {
          await this.updateTier({
            clinicUrl: this.clinicUrl,
            tierId: this.tier.id,
            data: {
              name: this.form.name.trim(),
              rank: Number(this.form.rank),
              thresholdAmount: Number(this.form.thresholdAmount),
              voucherBatchId: this.form.voucherBatchId,
              isActive: !!this.form.isActive,
            },
          });
        } else {
          const data = {
            name: this.form.name.trim(),
            rank: Number(this.form.rank),
            thresholdAmount: Number(this.form.thresholdAmount),
          };
          if (this.form.voucherBatchId != null) data.voucherBatchId = this.form.voucherBatchId;
          await this.createTier({ clinicUrl: this.clinicUrl, data });
        }
        this.$emit("saved");
      } catch (error) {
        // The rank-uniqueness clash (and any other BE validation failure,
        // e.g. a cross-clinic voucherBatchId) comes back as a Thai
        // BadRequestError message — surface it verbatim rather than a
        // generic failure string, it tells the user exactly what to fix.
        this.errorMessage = tierErrorMessage(error) || "บันทึกระดับลูกค้าไม่สำเร็จ";
      } finally {
        this.isSaving = false;
      }
    },
  },
  created() {
    this.fetchBatches({ clinicUrl: this.clinicUrl });
  },
};
</script>
